import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAuth, LOGIN_MUTATION, CHANGE_PASSWORD_MUTATION, VALIDAR_CODIGO_INGRESO, REENVIAR_CODIGO_INGRESO } from './AuthContext';
import { theme } from './theme';
import './office_panel.css';

export default function LoginPage() {
  const { user, login, updateUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Change password state
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmNewPass, setConfirmNewPass] = useState('');

  const [loginMut] = useMutation(LOGIN_MUTATION);
  const [changePassMut] = useMutation(CHANGE_PASSWORD_MUTATION);
  const [validarMut] = useMutation(VALIDAR_CODIGO_INGRESO);
  const [reenviarMut] = useMutation(REENVIAR_CODIGO_INGRESO);
  const [codigo, setCodigo] = useState('');
  const [aviso, setAviso] = useState('');

  // Detect if user already logged in but needs password change
  const mustChange = user?.mustChangePassword || false;
  // Personal nuevo (desde el 25/10/2026): primero valida su email con el codigo que le llega.
  const validarEmail = user?.validarEmail || false;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await login(loginMut, email, password);
      if (u.mustChangePassword || u.validarEmail) {
        // Save current pass for the change form
        setCurrentPass(password);
      }
    } catch (err) {
      setError(err.graphQLErrors?.[0]?.message || err.message);
    }
    setLoading(false);
  };

  const handleValidar = async (e) => {
    e.preventDefault();
    setError(''); setAviso('');
    setLoading(true);
    try {
      const { data } = await validarMut({ variables: { codigo: codigo.trim() } });
      updateUser(data.validarCodigoIngreso);
    } catch (err) {
      setError(err.graphQLErrors?.[0]?.message || err.message);
    }
    setLoading(false);
  };

  const handleReenviar = async () => {
    setError(''); setAviso('');
    try {
      const { data } = await reenviarMut();
      setAviso(data.reenviarCodigoIngreso ? `Te mandamos un código nuevo a ${user.email}.` : 'No se pudo mandar el código. Probá de nuevo en un rato.');
    } catch (err) {
      setError(err.graphQLErrors?.[0]?.message || err.message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPass !== confirmNewPass) { setError('Las contraseñas no coinciden'); return; }
    if (newPass.length < 6) { setError('Mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      const { data } = await changePassMut({ variables: { currentPassword: currentPass, newPassword: newPass } });
      updateUser(data.changePassword);
    } catch (err) {
      setError(err.graphQLErrors?.[0]?.message || err.message);
    }
    setLoading(false);
  };

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    fontFamily: theme.container.fontFamily
  };

  const cardStyle = { ...theme.card, maxWidth: '420px', width: '100%', padding: '40px 36px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' };
  const logoStyle = { textAlign: 'center', marginBottom: '28px' };
  const inputStyle = { ...theme.input, width: '100%', boxSizing: 'border-box' };
  const errorStyle = { background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '12px', border: '1px solid #fecaca' };

  // ─── Validar email (primer ingreso) ─────────────────
  if (validarEmail) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={logoStyle}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>✉️</div>
            <h2 style={{ ...theme.title, fontSize: '1.3rem', marginBottom: '4px' }}>Validá tu email</h2>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Te mandamos un código de 6 números a <b>{user.email}</b>. Vence en 30 minutos.</p>
          </div>
          <form onSubmit={handleValidar}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#555' }}>Código</label>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))} style={{ ...inputStyle, letterSpacing: '6px', fontSize: '1.2rem', textAlign: 'center' }} required autoFocus />
            {error && <div style={errorStyle}>{error}</div>}
            {aviso && <div style={{ ...errorStyle, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' }}>{aviso}</div>}
            <button type="submit" disabled={loading || codigo.length !== 6} style={{ ...theme.button, width: '100%', opacity: loading || codigo.length !== 6 ? 0.6 : 1 }}>{loading ? 'Validando...' : 'Validar'}</button>
          </form>
          <div style={{ textAlign: 'center', marginTop: '14px' }}>
            <button type="button" onClick={handleReenviar} style={{ background: 'none', border: 'none', color: '#4f6ef7', cursor: 'pointer', fontSize: '0.85rem' }}>¿No te llegó? Mandarme otro código</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Change Password Screen ────────────────────────
  if (mustChange) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={logoStyle}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>🔐</div>
            <h2 style={{ ...theme.title, fontSize: '1.3rem', marginBottom: '4px' }}>Cambiar contraseña</h2>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Por seguridad, cambiá tu contraseña temporal en el primer ingreso.</p>
          </div>
          <form onSubmit={handleChangePassword}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#555' }}>Contraseña actual</label>
            <input type="password" value={currentPass} onChange={e => setCurrentPass(e.target.value)} style={inputStyle} required />
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#555' }}>Nueva contraseña</label>
            <input type="password" placeholder="Mínimo 6 caracteres" value={newPass} onChange={e => setNewPass(e.target.value)} style={inputStyle} required minLength={6} />
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#555' }}>Confirmar nueva contraseña</label>
            <input type="password" placeholder="Repetir contraseña" value={confirmNewPass} onChange={e => setConfirmNewPass(e.target.value)} style={inputStyle} required />
            {error && <div style={errorStyle}>{error}</div>}
            <button type="submit" disabled={loading} style={{ ...theme.button, width: '100%', opacity: loading ? 0.6 : 1 }}>{loading ? 'Guardando...' : 'Guardar nueva contraseña'}</button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Login Screen ──────────────────────────────────
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={logoStyle}>
          <img src="/images/logo512.png" alt="NAWI" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', display: 'block', margin: '0 auto' }} />
          <h2 style={{ ...theme.title, fontSize: '1.3rem', marginBottom: '4px', marginTop: '12px' }}>Nawi Office</h2>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>Panel de gestión de empresas</p>
        </div>

        <form onSubmit={handleLogin}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#555' }}>Email</label>
          <input type="email" placeholder="tu@empresa.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} required autoFocus />
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '4px', color: '#555' }}>Contraseña</label>
          <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} required />
          {error && <div style={errorStyle}>{error}</div>}
          <button type="submit" disabled={loading} style={{ ...theme.button, width: '100%', opacity: loading ? 0.6 : 1 }}>{loading ? 'Ingresando...' : 'Ingresar'}</button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <p style={{ color: '#999', fontSize: '0.82rem' }}>Las cuentas se crean desde el panel de superadmin</p>
        </div>
      </div>
    </div>
  );
}